import { z } from "zod";
import { MAX_AGE, MIN_AGE } from "@/components/filters/AgeRangeSelector";
import {
  MAX_DISTANCE_KM,
  MIN_DISTANCE_KM,
} from "@/components/filters/DistanceSelector";
import {
  optionSchema,
  optionWithIdSchema,
} from "@/components/onboarding/data-form/SharedZodSchemas";
import {
  ProfileSearchFilters,
  SearchSortBy,
  DrinkingStatus,
  Ethnicity,
  OpenTo,
  SmokerStatus,
  TattooStatus,
  RecentSearchFilters,
} from "@/generated-api";
import { MAX_HEIGHT, MIN_HEIGHT } from "./HeightRangeSelector";
import { cityLabel } from "../onboarding/data-form/CitySelector";

export const searchFormSchema = z.object({
  city: z.array(optionSchema).optional(),
  distanceKm: z.number().min(MIN_DISTANCE_KM).max(MAX_DISTANCE_KM),
  age: z.array(z.number().min(MIN_AGE).max(MAX_AGE)),
  height: z.array(z.number().min(MIN_HEIGHT).max(MAX_HEIGHT)),
  languages: z
    .array(optionWithIdSchema)
    .max(5, "Select at most 5 languages")
    .optional(),
  nationalities: z
    .array(optionWithIdSchema)
    .max(5, "Select at most 5 nationalities")
    .optional(),
  ethnicity: z.nativeEnum(Ethnicity).optional().nullable(),
  openTo: z.array(z.nativeEnum(OpenTo)).optional(),
  smoker: z.array(z.nativeEnum(SmokerStatus)).optional(),
  tattoos: z.array(z.nativeEnum(TattooStatus)).optional(),
  drinking: z.array(z.nativeEnum(DrinkingStatus)).optional(),
});

export type SearchFormSchema = z.infer<typeof searchFormSchema>;

export const DEFAULT_FORM_VALUES: Partial<SearchFormSchema> = {
  distanceKm: MIN_DISTANCE_KM,
  age: [MIN_AGE, MAX_AGE],
  height: [MIN_HEIGHT, MAX_HEIGHT],
};

export function mapRecentSearchToFormValues(
  recentSearch: RecentSearchFilters | null
): Partial<SearchFormSchema> {
  if (!recentSearch) return DEFAULT_FORM_VALUES;

  return {
    city:
      recentSearch.cities?.map((c) => ({ label: cityLabel(c), value: c.id })) ||
      undefined,
    distanceKm: recentSearch.distance_km ?? MIN_DISTANCE_KM,
    age: recentSearch.age_range
      ? [recentSearch.age_range.minimum, recentSearch.age_range.maximum]
      : [MIN_AGE, MAX_AGE],
    height: recentSearch.height_range
      ? [recentSearch.height_range.minimum, recentSearch.height_range.maximum]
      : [MIN_HEIGHT, MAX_HEIGHT],
    languages:
      recentSearch.languages?.map((lang) => ({
        id: lang.id,
        label: lang.name,
        value: lang.id,
      })) || undefined,
    nationalities:
      recentSearch.nationalities?.map((nat) => ({
        id: nat.id,
        label: nat.nationality,
        value: nat.id,
      })) || undefined,
    ethnicity: recentSearch.ethnicity || undefined,
    openTo: recentSearch.open_to || undefined,
    smoker: recentSearch.smoker || undefined,
    tattoos: recentSearch.tattoos || undefined,
    drinking: recentSearch.drinking || undefined,
  };
}

export function searchFormToFilters(
  values: SearchFormSchema,
  sortBy: SearchSortBy
): ProfileSearchFilters {
  return {
    sort_by: sortBy,
    cities: values.city?.map((city) => city.value),
    distance_km: values.distanceKm,
    age_range: { minimum: values.age[0], maximum: values.age[1] },
    languages: values.languages?.map((lang) => lang.id),
    nationalities: values.nationalities?.map((nat) => nat.id),
    ethnicity: values.ethnicity || undefined,
    height_range: { minimum: values.height[0], maximum: values.height[1] },
    open_to: values.openTo,
    smoker: values.smoker,
    tattoos: values.tattoos,
    drinking: values.drinking,
  };
}
